import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  CardHeader,
  CardContent,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import { DateRange } from '@mui/x-date-pickers-pro'
import { Payments as PaymentsIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import { MuiCard, MuiTextField, LoansList } from 'components'
import { useGetBaseLoansQuery, Order_By } from 'api/generated'
import { formatCurrency } from 'utils/currency'
import { getLoansAggregateWhere, ALL, getLoansFilterPath } from 'utils/chart'
import { DictionariesQueryProps } from 'screens/Authenticated/Dashboard/Admin'
import { PaginationModelType, defaultPaginationModel } from 'utils/dataGrid'

export const TODAY_DATE_RANGE: DateRange<Date> = [new Date(), new Date()]

const RecentLoansList: React.FC<DictionariesQueryProps> = ({
  dictionariesQuery
}: DictionariesQueryProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [locationId, setLocationId] = useState(ALL)
  const [userId, setUserId] = useState(ALL)
  const [paginationModel, setPaginationModel] = useState<PaginationModelType>(
    defaultPaginationModel
  )
  const { page, pageSize } = paginationModel
  const { data, loading, error, refetch } = useGetBaseLoansQuery({
    variables: {
      where: getLoansAggregateWhere(TODAY_DATE_RANGE, userId, locationId),
      limit: pageSize,
      offset: pageSize * page,
      order_by: { created_at: Order_By.Desc }
    }
  })
  const dictionariesData = dictionariesQuery?.data
  const aggregate = data?.loansAggregate?.aggregate
  const aggregateSum = aggregate?.sum
  const totalCount = aggregate?.totalCount

  const handleChangeLocationId = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setLocationId(ev.target.value)
    },
    []
  )

  const handleChangeUserId = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setUserId(ev.target.value)
    },
    []
  )

  const handleShowLoansInDetailTable = useCallback(() => {
    const selectedUser = dictionariesData?.users?.find(
      user => user?.id === userId
    )
    const selectedLocation = dictionariesData?.locations?.find(
      location => location?.id === locationId
    )
    const loansFilterPath = getLoansFilterPath(
      TODAY_DATE_RANGE,
      selectedUser?.full_name,
      selectedLocation?.name
    )

    navigate(loansFilterPath)
  }, [dictionariesData, userId, locationId, navigate])

  return (
    <MuiCard>
      <CardHeader
        title={t('recentLoans')}
        subheader={
          <Stack
            direction='row'
            spacing={1}
            divider={<Divider orientation='vertical' flexItem />}
          >
            <Tooltip title={t('totalLoans')}>
              <Typography variant='body1'>{totalCount || 0}</Typography>
            </Tooltip>

            <Tooltip title={t('totalAmount')}>
              <Typography variant='body1'>
                {aggregateSum
                  ? formatCurrency({
                      amount: aggregateSum?.amount || 0
                    })
                  : ''}
              </Typography>
            </Tooltip>
          </Stack>
        }
        action={
          <Tooltip title={t('showInDetail')}>
            <IconButton onClick={handleShowLoansInDetailTable}>
              <PaymentsIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={2} direction='row'>
            <MuiTextField
              select
              label={t('user')}
              size='small'
              onChange={handleChangeUserId}
              value={userId}
            >
              <MenuItem value={ALL}>{t(ALL)}</MenuItem>
              {dictionariesData?.users?.map(user => {
                return (
                  <MenuItem
                    key={user?.id}
                    value={user?.id}
                  >{`${user?.full_name}`}</MenuItem>
                )
              })}
            </MuiTextField>

            <MuiTextField
              select
              label={t('location')}
              size='small'
              onChange={handleChangeLocationId}
              value={locationId}
            >
              <MenuItem value={ALL}>{t(ALL)}</MenuItem>
              {dictionariesData?.locations?.map(location => {
                return (
                  <MenuItem key={location?.id} value={location?.id}>
                    {location?.name}
                  </MenuItem>
                )
              })}
            </MuiTextField>
          </Stack>

          <LoansList
            data={data}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            loading={loading}
            refetch={refetch}
            error={error}
          />
        </Stack>
      </CardContent>
    </MuiCard>
  )
}

export default RecentLoansList
